import React, { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import api from "../api";
import "./Upload.css";
import { useAuth } from "../AuthContext";

async function downloadTemplate() {
  const resp = await api.get("/upload/template", { responseType: "blob" });
  const url  = URL.createObjectURL(resp.data);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = "commission_upload_template.xlsx";
  a.click();
  URL.revokeObjectURL(url);
}

export default function Upload() {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const onDrop = useCallback(accepted => { if (accepted[0]) setFile(accepted[0]); }, []);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"], "application/vnd.ms-excel": [".xls"] },
    multiple: false
  });

  const handleUpload = async () => {
    if (!file) return;
    setLoading(true); setResult(null);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await api.post("/upload/excel", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
    } catch (err) {
      setResult({ error: err.response?.data?.detail || "Upload failed" });
    } finally { setLoading(false); }
  };

  const handleDownloadTemplate = async () => {
    setDownloading(true);
    try {
      await downloadTemplate();
    } catch (err) {
      alert("Failed to download template");
    } finally {
      setDownloading(false);
    }
  };

  const { user } = useAuth();

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Upload Data</h1>
          <p className="page-subtitle">Import commissions from Excel</p>
        </div>
      </div>

      <div className="upload-card">
        <div {...getRootProps()} className={`dropzone ${isDragActive ? "active" : ""} ${file ? "has-file" : ""}`}>
          <input {...getInputProps()} />
          <div className="dropzone-icon">{file ? "📄" : "📂"}</div>
          {file
            ? <><p className="dropzone-filename">{file.name}</p><p className="dropzone-hint">Click or drag to replace</p></>
            : <><p className="dropzone-text">{isDragActive ? "Drop it!" : "Drag & drop your Excel file here"}</p><p className="dropzone-hint">or click to browse · .xlsx / .xls</p></>
          }
        </div>

        {file && (
          <button className="btn-primary upload-btn" onClick={handleUpload} disabled={loading}>
            {loading ? "Processing..." : "Upload & Import"}
          </button>
        )}

        {result && !result.error && (
          <div className="upload-result success">
            <h3>✅ Upload Complete</h3>
            <p><strong>{result.loans_created}</strong> loans created</p>
            <p><strong>{result.commissions_created}</strong> commissions created</p>
            {result.errors?.length > 0 && (
              <div className="upload-errors">
                <p><strong>Warnings ({result.errors.length}):</strong></p>
                {result.errors.map((e, i) => <p key={i} className="error-line">• {e}</p>)}
              </div>
            )}
          </div>
        )}

        {result?.error && (
          <div className="upload-result error-result">
            <h3>❌ Upload Failed</h3>
            <p>{result.error}</p>
          </div>
        )}
      </div>

      <div className="template-card">
        <div className="template-card-header">
          <h2>Required Excel Format</h2>
          {user?.role === "admin" && (
            <button className="btn-secondary btn-sm" onClick={handleDownloadTemplate} disabled={downloading}>
              {downloading ? "Downloading..." : "↓ Download Template"}
            </button>
          )}
        </div>
        <p>Your Excel file must include these required column headers:</p>
        <div className="columns-list">
          {["loan_number","borrower_last_name","loan_amount","loan_type","close_date","loan_officer_nmls","lead_source","lien_position"].map(col => (
            <span key={col} className="column-chip mono">{col}</span>
          ))}
        </div>
        <p style={{ marginTop: "12px", marginBottom: "4px", fontWeight: 600 }}>Optional columns:</p>
        <div className="columns-list">
          {["funding_date","loan_purpose"].map(col => (
            <span key={col} className="column-chip mono" style={{ opacity: 0.7 }}>{col}</span>
          ))}
        </div>
        <p className="template-note">
          <strong>lead_source</strong> must be one of: <span className="mono">self_generated</span>, <span className="mono">partner_referral</span>, <span className="mono">client_referral</span>, <span className="mono">company_referral</span>.<br />
          <strong>lien_position</strong> must be one of: <span className="mono">first</span>, <span className="mono">second</span>.<br />
          <strong>loan_officer_nmls</strong> must match the NMLS number of an existing loan officer in the system.<br />
          Commission is automatically calculated from the loan officer's current tier and lead source.
        </p>
      </div>
    </div>
  );
}
